// client/src/components/templates/anamnese/TemplateAnamneseForm.js
import React, { useState, useEffect } from 'react';
import { useMutateTemplateAnamnese } from '../../../hooks/useTemplateAnamnese';
import { useAuth } from '../../../hooks/useAuth'; // Importação necessária
import { isMatrizAdmin as checkIsMatrizAdmin } from '../../../utils/permissions';
import { Save, XCircle, Loader2, PlusCircle, Trash2, ArrowUp, ArrowDown, Type, MessageSquare, CheckSquare, Image as ImageIcon, Minus, ToggleRight, Radio, FileText } from 'lucide-react';
import GerarTextoIABox from '../../ai/GerarTextoIABox';

// ✅ CORREÇÃO: Removido 'isPadrao' do estado inicial.
const initialState = { nome: '', descricao: '', conteudo: [], requerAssinatura: true };

const AnamneseItem = ({ item, index, onUpdate, onRemove, onMove, totalItems }) => {
    const handleOptionChange = (optIndex, optValue) => {
        const newOptions = item.options.map((opt, i) =>
            i === optIndex ? { ...opt, label: optValue } : opt
        );
        onUpdate(index, 'options', newOptions);
    };

    const addOption = () => onUpdate(index, 'options', [...(item.options || []), { id: Date.now(), label: 'Nova Opção' }]);
    const removeOption = (optIndex) => onUpdate(index, 'options', item.options.filter((_, i) => i !== optIndex));

    const moveOption = (optIndex, direction) => {
        const newOptions = [...item.options];
        const option = newOptions[optIndex];
        const newIndex = optIndex + direction;
        if (newIndex < 0 || newIndex >= newOptions.length) return;
        newOptions.splice(optIndex, 1);
        newOptions.splice(newIndex, 0, option);
        onUpdate(index, 'options', newOptions);
    };

    const renderItemOptions = () => {
        if (!['Opções', 'Multi Opções', 'Verdadeiro ou Falso'].includes(item.type)) return null;
        return (
            <div className="form-builder-options">
                <label className="options-label">Opções de Resposta:</label>
                {(item.options || []).map((opt, optIndex) => (
                    <div key={opt.id || optIndex} className="dynamic-list-item">
                        <div className="form-group">
                            <input
                                type="text"
                                value={opt.label}
                                onChange={(e) => handleOptionChange(optIndex, e.target.value)}
                                className="form-input"
                            />
                        </div>
                        <button type="button" onClick={() => moveOption(optIndex, -1)} disabled={optIndex === 0} className="action-icon-button">
                            <ArrowUp size={16} />
                        </button>
                        <button type="button" onClick={() => moveOption(optIndex, 1)} disabled={optIndex === item.options.length - 1} className="action-icon-button">
                            <ArrowDown size={16} />
                        </button>
                        <button type="button" onClick={() => removeOption(optIndex)} className="remove-item-button">
                            <Minus size={16} />
                        </button>
                    </div>
                ))}
                <button type="button" onClick={addOption} className="add-item-button">
                    <PlusCircle size={14} className="icon" /> Adicionar Opção
                </button>
            </div>
        );
    };

    return (
        <div className="form-builder-item">
            <div className="form-builder-item__header">
                <span className="form-builder-item__title">
                    Item {index + 1}: <span className="form-builder-item__type">{item.type}</span>
                </span>
                <div className="form-builder-item__controls">
                    <button type="button" onClick={() => onMove(index, -1)} disabled={index === 0} title="Mover para Cima">
                        <ArrowUp size={16} />
                    </button>
                    <button type="button" onClick={() => onMove(index, 1)} title="Mover para Baixo" disabled={index === (totalItems - 1)}>
                        <ArrowDown size={16} />
                    </button>
                    <button type="button" onClick={() => onRemove(index)} title="Remover Item">
                        <Trash2 size={16} />
                    </button>
                </div>
            </div>
            <div className="form-builder-item__body">
                <input
                    type="text"
                    placeholder={item.type.includes('Título') || item.type.includes('Separador') || item.type.includes('Texto') ? 'Digite o texto ou título' : 'Digite a pergunta...'}
                    value={item.label || ''}
                    onChange={(e) => onUpdate(index, 'label', e.target.value)}
                    className="form-input"
                />
                {renderItemOptions()}
                <div className="form-group-checkbox">
                    <input
                        type="checkbox"
                        id={`obs-${item.id}`}
                        checked={item.requerObservacao || false}
                        onChange={(e) => onUpdate(index, 'requerObservacao', e.target.checked)}
                    />
                    <label htmlFor={`obs-${item.id}`}>Habilitar campo de observação</label>
                </div>
            </div>
        </div>
    );
};


const TemplateAnamneseForm = ({ onCloseForm, current }) => {
    const { user } = useAuth();
    const { addTemplateAnamnese, updateTemplateAnamnese, isPendingAnamnese: isMutating } = useMutateTemplateAnamnese();
    const [template, setTemplate] = useState(initialState);
    const isMatrizAdmin = checkIsMatrizAdmin(user) && !user?.account && !user?.franquia;

    useEffect(() => {
        if (current) {
            const conteudoSanitizado = (Array.isArray(current.conteudo) ? current.conteudo : []).map(item => ({
                ...item,
                options: (Array.isArray(item.options) ? item.options : []).map(opt => 
                    typeof opt === 'string' ? { id: Date.now() + Math.random(), label: opt } : opt
                ),
            }));
            setTemplate({ ...current, conteudo: conteudoSanitizado });
        } else {
            setTemplate(initialState);
        }
    }, [current]);

    const onChange = (e) => {
        const { name, value, type, checked } = e.target;
        setTemplate(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    };

    const addItem = (type) => setTemplate((prev) => {
        let newOptions = [];
        if (type === 'Verdadeiro ou Falso') {
            newOptions = [
                { id: Date.now() + 1, label: 'Sim' },
                { id: Date.now() + 2, label: 'Não' },
            ];
        } else if (type === 'Opções' || type === 'Multi Opções') {
            newOptions = [{ id: Date.now() + 1, label: 'Nova Opção' }];
        }
        return {
            ...prev,
            conteudo: [
                ...(prev.conteudo || []),
                { id: Date.now(), label: '', type, options: newOptions, requerObservacao: false },
            ],
        };
    });

    const updateItem = (index, field, value) => {
        const newConteudo = [...template.conteudo];
        newConteudo[index] = { ...newConteudo[index], [field]: value };
        setTemplate({ ...template, conteudo: newConteudo });
    };

    const removeItem = (index) => setTemplate({ ...template, conteudo: template.conteudo.filter((_, i) => i !== index) });

    const moveItem = (index, dir) => {
        const newConteudo = [...template.conteudo];
        const item = newConteudo[index];
        const newIndex = index + dir;
        if (newIndex < 0 || newIndex >= newConteudo.length) return;
        newConteudo.splice(index, 1);
        newConteudo.splice(newIndex, 0, item);
        setTemplate({ ...template, conteudo: newConteudo });
    };

    const onSubmit = (e) => {
        e.preventDefault();
        const payload = {
            ...template,
            conteudo: (template.conteudo || []).map(item => ({
                ...item,
                options: (item.options || []).map(opt => typeof opt === 'object' ? opt.label : opt),
            })),
        };
        
        if (!isMatrizAdmin) {
            delete payload.isPadrao;
        }
        
        const options = {
            onSuccess: () => {
                onCloseForm();
            },
        };

        if (current) {
            updateTemplateAnamnese.mutate({ id: template._id, templateData: payload }, options);
        } else {
            addTemplateAnamnese.mutate(payload, options);
        }
    };

    return (
        <form onSubmit={onSubmit} noValidate>
                    <div className="form-header">
                        <h3 className="form-title">{current ? 'Editar' : 'Novo'} Template de Anamnese</h3>
                        <button type="button" onClick={onCloseForm} title="Fechar" className="form-close-button">
                            <XCircle size={24} />
                        </button>
                    </div>

                    <fieldset className="form-section">
                        <legend className="form-section-title">Informações Gerais</legend>
                        <div className="form-grid-container">
                            <div className="form-group full-width">
                                <label htmlFor="nome">Nome*</label>
                                <input type="text" id="nome" name="nome" value={template.nome || ''} onChange={onChange} required className="form-input" />
                            </div>
                            <div className="form-group full-width">
                                <label htmlFor="descricao">Descrição</label>
                                <input type="text" id="descricao" name="descricao" value={template.descricao || ''} onChange={onChange} className="form-input" />
                            </div>
                            {/* ✅ CORREÇÃO: Adiciona o toggle para 'isPadrao' apenas para o admin da matriz */}
                            {isMatrizAdmin && (
                                <div className="form-group-checkbox full-width">
                                    <input type="checkbox" name="isPadrao" id="isPadrao" checked={template.isPadrao || false} onChange={onChange} />
                                    <label htmlFor="isPadrao">Este é um template Padrão Global</label>
                                </div>
                            )}
                        </div>
                    </fieldset>

                    <fieldset className="form-section">
                        <legend className="form-section-title">Construtor do Formulário</legend>
                        <GerarTextoIABox
                            endpoint="/templates/anamnese/gerar-ia"
                            placeholder='Descreva a anamnese — ex: "avaliação pré-procedimento estético facial, foco em alergias e histórico de pele"'
                            successMessage="Ficha gerada! Revise os itens antes de salvar."
                            onGenerated={(itensGerados) => setTemplate(prev => ({ ...prev, conteudo: itensGerados }))}
                        />
                        <div className="form-builder-container">
                            {template.conteudo && template.conteudo.length > 0 ? (
                                template.conteudo.map((item, i) => (
                                    <AnamneseItem key={item.id || i} item={{...item, totalItems: template.conteudo.length}} index={i} onUpdate={updateItem} onRemove={removeItem} onMove={moveItem} />
                                ))
                            ) : (
                                <div className="table-empty-state">Comece a adicionar itens abaixo.</div>
                            )}
                        </div>
                        <div className="form-builder-add-bar">
                             <button type="button" onClick={() => addItem('Título')} className="add-item-button"><Type size={16} className="icon" /> Título</button>
                             <button type="button" onClick={() => addItem('Texto Simples')} className="add-item-button"><MessageSquare size={16} className="icon" /> Texto</button>
                             <button type="button" onClick={() => addItem('Separador')} className="add-item-button"><Minus size={16} className="icon" /> Separador</button>
                             <button type="button" onClick={() => addItem('Resposta em Texto')} className="add-item-button"><FileText size={16} className="icon" /> Resposta em Texto</button>
                             <button type="button" onClick={() => addItem('Verdadeiro ou Falso')} className="add-item-button"><ToggleRight size={16} className="icon" /> Sim/Não</button>
                             <button type="button" onClick={() => addItem('Opções')} className="add-item-button"><Radio size={16} className="icon" /> Opção Única</button>
                             <button type="button" onClick={() => addItem('Multi Opções')} className="add-item-button"><CheckSquare size={16} className="icon" /> Múltipla Escolha</button>
                             <button type="button" onClick={() => addItem('Galeria')} className="add-item-button"><ImageIcon size={16} className="icon" /> Galeria</button>
                        </div>
                    </fieldset>

                    <fieldset className="form-section">
                        <legend className="form-section-title">Finalização</legend>
                        <div className="form-group-checkbox">
                            <input type="checkbox" name="requerAssinatura" id="requerAssinatura" checked={template.requerAssinatura} onChange={onChange} />
                            <label htmlFor="requerAssinatura">Exigir assinatura digital do cliente ao final</label>
                        </div>
                    </fieldset>

                    <div className="form-actions">
                        <button type="button" onClick={onCloseForm} className="action-button action-button-secondary">
                            <XCircle size={18} className="icon" /> Cancelar
                        </button>
                        <button type="submit" disabled={isMutating} className="action-button action-button-primary">
                            {isMutating ? <Loader2 className="animate-spin icon" /> : <Save size={18} className="icon" />}
                            {current ? 'Atualizar Template' : 'Salvar Template'}
                        </button>
                    </div>
        </form>
    );
};

export default TemplateAnamneseForm;